import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "convex/react";
import { api } from "@jourfx/convex/client";
import { Link } from "@tanstack/react-router";
import { Button, Card, CardContent, CardHeader, CardTitle, Input, Label } from "@jourfx/ui";
import { Copy, Plus, Search } from "lucide-react";
import { useDateFormat } from "@/hooks/useDateFormat";

type Id<T extends string> = string & { __tableName: T };

export function VersichertenList() {
  const { t } = useTranslation("versicherte");
  const { formatShortDate } = useDateFormat();
  const apiRef = api as any;
  const [search, setSearch] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [createdCredentials, setCreatedCredentials] = useState<{ accessCode: string } | null>(null);

  const versicherte = useQuery(apiRef.versichertenProfiles.index.list, {
    search: search.trim() || undefined,
    limit: 200,
  }) as
    | Array<{
        _id: string;
        firstName: string;
        lastName: string;
        email?: string;
        phone?: string;
        createdAt: number;
      }>
    | undefined;

  const createVersichertenprofil = useMutation(apiRef.versichertenProfiles.index.create);

  const handleCreate = async () => {
    if (!firstName.trim() || !lastName.trim()) return;
    setIsCreating(true);
    try {
      const result = await createVersichertenprofil({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim() || undefined,
        phone: phone.trim() || undefined,
      });
      setCreatedCredentials({ accessCode: result.accessCode });
      setFirstName("");
      setLastName("");
      setEmail("");
      setPhone("");
    } catch (error) {
      console.error("Failed to create versicherten profile", error);
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{t("title")}</h1>
          <p className="text-muted-foreground">
            {t("subtitle")}
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t("newProfile.title")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="versicherte-first-name">{t("newProfile.firstName")}</Label>
              <Input
                id="versicherte-first-name"
                value={firstName}
                onChange={(event) => setFirstName(event.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="versicherte-last-name">{t("newProfile.lastName")}</Label>
              <Input
                id="versicherte-last-name"
                value={lastName}
                onChange={(event) => setLastName(event.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="versicherte-email">{t("newProfile.email")}</Label>
              <Input
                id="versicherte-email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="versicherte-phone">{t("newProfile.phone")}</Label>
              <Input
                id="versicherte-phone"
                value={phone}
                onChange={(event) => setPhone(event.target.value)}
              />
            </div>
          </div>
          <Button onClick={handleCreate} disabled={isCreating || !firstName.trim() || !lastName.trim()}>
            <Plus className="mr-2 h-4 w-4" />
            {t("newProfile.create")}
          </Button>
        </CardContent>
      </Card>

      {createdCredentials && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
          <h3 className="font-semibold text-green-800 mb-2">{t("credentials.created")}</h3>
          <div className="space-y-2">
            <div className="flex items-center justify-between rounded-md border border-green-200 p-3">
              <span className="text-sm text-green-700">{t("credentials.accessCode")}</span>
              <div className="flex items-center gap-2">
                <span className="font-mono text-sm font-bold">{createdCredentials.accessCode}</span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigator.clipboard.writeText(createdCredentials.accessCode)}
                >
                  <Copy className="mr-2 h-4 w-4" />
                  {t("credentials.copy")}
                </Button>
              </div>
            </div>
          </div>
          <p className="text-green-600 text-xs mt-2">{t("credentials.hint")}</p>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setCreatedCredentials(null)}
            className="mt-2 text-green-700 hover:text-green-900"
          >
            {t("credentials.dismiss")}
          </Button>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t("list.title")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={t("list.search")}
              className="pl-9"
            />
          </div>

          <div className="overflow-x-auto border rounded-md">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr className="text-left">
                  <th className="px-3 py-2 font-medium">{t("list.columns.name")}</th>
                  <th className="px-3 py-2 font-medium">{t("list.columns.email")}</th>
                  <th className="px-3 py-2 font-medium">{t("list.columns.phone")}</th>
                  <th className="px-3 py-2 font-medium">{t("list.columns.created")}</th>
                  <th className="px-3 py-2 font-medium" />
                </tr>
              </thead>
              <tbody>
                {versicherte?.map((versicherter) => (
                  <tr key={versicherter._id} className="border-t">
                    <td className="px-3 py-2">
                      {versicherter.firstName} {versicherter.lastName}
                    </td>
                    <td className="px-3 py-2">{versicherter.email || "-"}</td>
                    <td className="px-3 py-2">{versicherter.phone || "-"}</td>
                    <td className="px-3 py-2">
                      {formatShortDate(versicherter.createdAt)}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Link
                        to="/versicherte/$versicherteId"
                        params={{ versicherteId: versicherter._id as Id<"versichertenProfiles"> }}
                        className="text-primary hover:underline"
                      >
                        {t("list.details")}
                      </Link>
                    </td>
                  </tr>
                ))}
                {versicherte?.length === 0 && (
                  <tr>
                    <td className="px-3 py-6 text-muted-foreground" colSpan={5}>
                      {t("list.empty")}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
